import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Grid3X3, Maximize2 } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * PhotoGallery - Responsive photo gallery with lightbox
 * Features:
 * - Thumbnail grid in card
 * - Full-screen lightbox with navigation
 * - Keyboard navigation (arrows, escape)
 * - Touch swipe support
 * - Fallback placeholder for empty galleries
 */
const PhotoGallery = ({ 
  photos = [], 
  propertyTitle = "Immobile",
  showInCard = true,
  maxThumbnails = 4 
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentIndex]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  const openLightbox = (index = 0) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  // Get valid image URL
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return url;
    if (url.startsWith('http')) return url;
    if (url.startsWith('simulated://')) {
      // Return placeholder for simulated uploads
      return null;
    }
    return url;
  };

  // Fallback placeholder
  const PlaceholderImage = ({ className = "" }) => (
    <div className={`bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${className}`}>
      <div className="text-center text-gray-400">
        <Grid3X3 className="h-8 w-8 mx-auto mb-2" />
        <span className="text-xs">Foto non disponibile</span>
      </div>
    </div>
  );

  // No photos fallback
  if (!photos || photos.length === 0) {
    return (
      <div className="bg-gray-100 rounded-lg p-6 text-center" data-testid="no-photos-placeholder">
        <Grid3X3 className="h-12 w-12 mx-auto text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">Nessuna foto disponibile</p>
      </div>
    );
  }

  const visiblePhotos = photos.slice(0, maxThumbnails);
  const remainingCount = photos.length - maxThumbnails;

  return (
    <>
      {/* Thumbnail Grid in Card */}
      {showInCard && (
        <div className="space-y-2" data-testid="photo-gallery-thumbnails">
          {/* Main Image */}
          <div 
            className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group"
            onClick={() => openLightbox(0)}
          >
            {getImageUrl(photos[0]) ? (
              <img 
                src={getImageUrl(photos[0])} 
                alt={`${propertyTitle} - Foto principale`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <PlaceholderImage className="w-full h-full" />
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <Maximize2 className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {/* Photo count badge */}
            <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center">
              <Grid3X3 className="h-3 w-3 mr-1" />
              {photos.length} foto
            </div>
          </div>

          {/* Thumbnail Row */}
          {photos.length > 1 && (
            <div className="grid grid-cols-4 gap-1">
              {visiblePhotos.slice(1).map((photo, idx) => (
                <div 
                  key={idx}
                  className="aspect-square rounded overflow-hidden cursor-pointer relative group"
                  onClick={() => openLightbox(idx + 1)}
                >
                  {getImageUrl(photo) ? (
                    <img 
                      src={getImageUrl(photo)} 
                      alt={`${propertyTitle} - Foto ${idx + 2}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                  ) : (
                    <PlaceholderImage className="w-full h-full" />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                </div>
              ))}
              {/* "+X more" overlay on last thumbnail */}
              {remainingCount > 0 && (
                <div 
                  className="aspect-square rounded overflow-hidden cursor-pointer relative"
                  onClick={() => openLightbox(maxThumbnails)}
                >
                  {getImageUrl(photos[maxThumbnails - 1]) ? (
                    <img 
                      src={getImageUrl(photos[maxThumbnails - 1])} 
                      alt={`${propertyTitle} - Foto ${maxThumbnails}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <PlaceholderImage className="w-full h-full" />
                  )}
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white font-semibold">+{remainingCount}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* View All Button */}
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={() => openLightbox(0)}
            data-testid="view-all-photos-btn"
          >
            <Grid3X3 className="h-4 w-4 mr-2" />
            Visualizza tutte le foto ({photos.length})
          </Button>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="photo-lightbox"
        >
          {/* Close Button */}
          <button 
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            onClick={closeLightbox}
            data-testid="lightbox-close-btn"
          >
            <X className="h-6 w-6 text-white" />
          </button>

          {/* Photo Counter */}
          <div className="absolute top-4 left-4 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
            {currentIndex + 1} / {photos.length}
          </div>

          {/* Navigation Arrows */}
          {photos.length > 1 && (
            <>
              <button 
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                data-testid="lightbox-prev-btn"
              >
                <ChevronLeft className="h-8 w-8 text-white" />
              </button>
              <button 
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                data-testid="lightbox-next-btn"
              >
                <ChevronRight className="h-8 w-8 text-white" />
              </button>
            </>
          )}

          {/* Main Image */}
          <div 
            className="max-w-5xl max-h-[80vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {getImageUrl(photos[currentIndex]) ? (
              <img 
                src={getImageUrl(photos[currentIndex])} 
                alt={`${propertyTitle} - Foto ${currentIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            ) : (
              <div className="w-96 h-64 bg-gray-800 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Grid3X3 className="h-16 w-16 mx-auto mb-4" />
                  <p>Foto non disponibile</p>
                  <p className="text-sm mt-1">Upload simulato</p>
                </div>
              </div>
            )}
          </div>

          {/* Thumbnail Strip */}
          {photos.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 max-w-full px-4 overflow-x-auto">
              {photos.map((photo, idx) => (
                <button
                  key={idx}
                  className={`w-16 h-16 rounded overflow-hidden flex-shrink-0 border-2 transition-all ${
                    idx === currentIndex ? 'border-white scale-105' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                >
                  {getImageUrl(photo) ? (
                    <img 
                      src={getImageUrl(photo)} 
                      alt={`Miniatura ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <Grid3X3 className="h-4 w-4 text-gray-500" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PhotoGallery;
